import React from "react";
import { Box, useColorModeValue, Stack, CircularProgress, useBreakpointValue, useTheme, Progress, Text, Image } from "@chakra-ui/react";
import { useTileProps } from "hooks";
import { TextOverflowTooltip } from "screens/landing/components/TextOverflowTooltip";
import { ShowMoreContainer } from "screens/common/components/ShowMoreContainer";
import { MarkdownItemView } from "screens/markdown/MarkdownItemView";
import { useCopyValue } from "hooks/useCopies";
import { BiLike, BiShareAlt, BiDotsVertical } from "react-icons/bi";
import { TbVersions } from "react-icons/tb";
import { ProjectReportPanelButton } from "screens/collection/components/ProjectReportPanelButton";
import { SmallActionButton } from "screens/content/contentCanvas/cell/SmallActionButton";
import { ImCancelCircle } from "react-icons/im";

interface TemporaryProjectSkeletonTileProps {
  ticker?: string;
  exchange?: string;
  companyName?: string;
  companyDescription?: string;
  logoUrl?: string;
}

const TemporaryProjectSkeletonTile: React.FC<TemporaryProjectSkeletonTileProps> = ({
  ticker,
  exchange,
  companyName,
  companyDescription,
  logoUrl,
}) => {
  const tileBgColor = useColorModeValue("white", "#161B25");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("primary.darkGray", "gray.300");
  const commonTileProps = useTileProps();
  const iconColor = useColorModeValue("500", "700");
  const tileBorderColor = useColorModeValue("gray.300", "gray.700");
  const progressColor = useColorModeValue("teal", "blue");
  const progressBgColor = useColorModeValue("gray.300", "gray.700");
  const progressTextColor = useColorModeValue("gray.600", "gray.300");
  const progressOpacity = useColorModeValue(".4", ".3");

  const loadingText = useCopyValue("copy_project_starting_message");
  const isMobile = useBreakpointValue({ base: true, md: false, lg: false }, { fallback: "md", ssr: false });
  const theme = useTheme();

  const currentTask = useCopyValue("copy_project_initializing_message");

  return (
    <Box
      {...commonTileProps}
      height={"unset"}
      position={"relative"}
      bgColor={tileBgColor}
      p="0"
      _hover={{
        boxShadow: "0 0 6px rgba(0, 0, 0, 0.1)",
      }}
      overflow="visible"
      textAlign="left"
      className="ch-project-landing-skeleton-tile">
      <Stack spacing={"0"} direction="column" h="100%" p="1rem">
        <Stack spacing={"1rem"} direction="column" h="full" mb="1rem">
          <Stack direction={"row"} spacing={"0.5rem"} alignItems="center" height="24px">
            <CircularProgress size={"20px"} isIndeterminate color={`orange.${iconColor}`} />
            <TextOverflowTooltip
              label={companyName || "Creating Project"}
              color={textColor}
              noOfLines={1}
              className="ch-project-landing-title"
            />
            <Text fontSize="xs" color={textColor}>
              ({exchange}:{ticker})
            </Text>
          </Stack>
          <Stack direction={"row"} spacing={"1rem"} alignItems="top" justifyContent={"space-between"}>
            <Box minWidth="10rem">
              {logoUrl ? (
                <Image src={logoUrl} alt={`company logo`} maxH={isMobile ? "25px" : "30px"} />
              ) : (
                <TextOverflowTooltip
                  className="ch-project-landing-default-tile-title"
                  label={companyName || ticker || "New Project"}
                  style={{ fontSize: "1.4rem", fontWeight: "bold", fontFamily: theme.fonts.logo, lineHeight: "1.2rem" }}
                />
              )}
            </Box>
            <Box color={textColor}>
              <ShowMoreContainer minHeight="6.5rem" height="4.5rem">
                <MarkdownItemView markdown={companyDescription || loadingText} />
              </ShowMoreContainer>
            </Box>
          </Stack>
        </Stack>
      </Stack>
      <Box
        zIndex={1}
        position={"absolute"}
        width="100%"
        left="0"
        bottom="2.7rem"
        borderColor={tileBorderColor}
        borderTopWidth="1px"
        p="10px"
        bgColor={tileBgColor}>
        <Stack width="100%" direction={"column"} textAlign={"end"} spacing={"0"}>
          <Stack direction="row" justifyContent="space-between" width="100%">
            <Text fontSize="11px" pb="0" width="100%" textAlign="start" color={progressTextColor}>
              {currentTask}
            </Text>
          </Stack>
          <Progress
            width="100%"
            alignSelf={"end"}
            mt={0}
            mb={0}
            size={"xs"}
            className="ch-progressbar"
            backgroundColor={progressBgColor}
            opacity={progressOpacity}
            borderRadius="full"
            isAnimated
            value={17}
            colorScheme={progressColor}
          />
        </Stack>
      </Box>
      <Stack
        direction="row"
        width="100%"
        justify="space-between"
        spacing={isMobile ? ".5rem" : "1rem"}
        px={isMobile ? ".5rem" : "1rem"}
        py={isMobile ? ".2rem" : ".5rem"}
        borderTop="1px solid"
        borderColor={borderColor}>
        <Stack direction="row" justify="space-between" spacing={"1rem"}>
          <SmallActionButton isDisabled iconSize="1.2rem" iconTypeName={TbVersions} />
          <SmallActionButton isDisabled iconSize="1rem" iconTypeName={BiLike} />
          <SmallActionButton isDisabled iconSize="1rem" iconTypeName={BiShareAlt} />
        </Stack>
        <Stack direction="row" justify="space-between" spacing={"1rem"}>
          <SmallActionButton isDisabled iconTypeName={ImCancelCircle} iconSize={"1rem"} iconColor="red.700" />
          <ProjectReportPanelButton showFirstReport isDisabled collectionId={""} />
          <SmallActionButton isDisabled iconSize="1rem" iconTypeName={BiDotsVertical} />
        </Stack>
      </Stack>
    </Box>
  );
};

export default TemporaryProjectSkeletonTile;
